// 契約台帳ビュー（§7-3「リスクで炙り出す」）。経営側の定期棚卸し用。
// リスクフラグ（滿期接近／自動續約／特殊條款／大額）を持つ契約を炙り出して一覧する。
import { useMemo, useState } from "react";
import { Badge } from "../../components/ui";
import {
  computeRiskFlags,
  daysUntil,
  isAutoRenew,
  isExpiringSoon,
  urgencyScore,
  type RiskKey,
} from "../../lib/risk";
import { formatAmount, formatDate } from "../../lib/format";
import { t } from "../../i18n/zh-TW";
import type { Contract } from "../../lib/types";

type Lens = "all" | RiskKey;

const LENSES: { key: Lens; label: string }[] = [
  { key: "all", label: t.ledger.lensAll },
  { key: "expiring", label: t.ledger.lensExpiring },
  { key: "auto_renew", label: t.ledger.lensAutoRenew },
  { key: "special", label: t.ledger.lensSpecial },
  { key: "large", label: t.ledger.lensLarge },
];

// 行ごとの「関鍵日期」（満了接近なら到期日＋残日数、自動更新なら続約日）。
function keyDateCell(c: Contract, today: Date) {
  if (isExpiringSoon(c, today) && c.period_end) {
    const d = daysUntil(c.period_end, today);
    return (
      <span>
        {t.ledger.keyDateExpiry} {formatDate(c.period_end)}
        {d !== null && (
          <span className="ml-1 text-xs text-red-600">（剩 {d} 天）</span>
        )}
      </span>
    );
  }
  if (isAutoRenew(c) && c.next_renewal_date) {
    return (
      <span>
        {t.ledger.keyDateRenewal} {formatDate(c.next_renewal_date)}
      </span>
    );
  }
  if (c.period_end) {
    return <span className="text-slate-500">{t.ledger.keyDateExpiry} {formatDate(c.period_end)}</span>;
  }
  return <span className="text-slate-400">—</span>;
}

export function ContractLedgerView({
  contracts,
  onOpen,
}: {
  contracts: Contract[];
  onOpen: (id: number) => void;
}) {
  const today = useMemo(() => new Date(), []);
  const [lens, setLens] = useState<Lens>("all");

  // フラグ付き契約のみを対象に炙り出し、緊急度順に並べる。
  const rows = useMemo(() => {
    const withFlags = contracts
      .map((c) => ({ c, flags: computeRiskFlags(c, today) }))
      .filter((r) => r.flags.length > 0);
    const filtered =
      lens === "all" ? withFlags : withFlags.filter((r) => r.flags.some((f) => f.key === lens));
    return filtered.sort((a, b) => urgencyScore(a.c, today) - urgencyScore(b.c, today));
  }, [contracts, lens, today]);

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-2">
        {LENSES.map((l) => (
          <button
            key={l.key}
            onClick={() => setLens(l.key)}
            className={`rounded-full border px-3 py-1 text-sm transition-colors ${
              lens === l.key
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>
      <p className="mb-4 text-xs text-slate-500">{t.ledger.thresholdNote}</p>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">
          {t.ledger.empty}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">{t.list.columnName}</th>
                <th className="px-4 py-3">{t.list.columnDept}</th>
                <th className="px-4 py-3">{t.list.columnCounterparty}</th>
                <th className="px-4 py-3 text-right">{t.list.columnAmount}</th>
                <th className="px-4 py-3">{t.ledger.columnRisk}</th>
                <th className="px-4 py-3">{t.ledger.columnKeyDate}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(({ c, flags }) => (
                <tr
                  key={c.id}
                  onClick={() => onOpen(c.id)}
                  className="cursor-pointer hover:bg-brand-50/40"
                >
                  <td className="px-4 py-3 text-sm font-medium text-slate-800">{c.contract_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{c.department}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{c.counterparty}</td>
                  <td className="px-4 py-3 text-right text-sm text-slate-800">
                    {formatAmount(c.amount, c.currency)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {flags.map((f) => (
                        <Badge key={f.key} tone={f.tone}>
                          {f.label}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{keyDateCell(c, today)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
